<template>
    <div :class="serviceClass">
        <button v-if="passedStops.length !== 0" type="button" class="more-stops-button" @click="showHideStops('prev')">
            <IconShowMore v-if="!showPrev" class="more-icon" width="3" height="15" />
            {{ t(`show_hide_previous_stops.${showPrev ? 'hide' : 'show'}`) }}
        </button>
        <ul class="stop-list no-bullet" :class="stopListClasses">
            <template v-if="passedStops.length !== 0 && showPrev">
                <li
                    v-for="(stop, i) in passedStops"
                    :key="i"
                    class="stop-item"
                    :class="[hours[i].arretNonDesservi ? 'is-unserved' : '']"
                >
                    <TripStop
                        :is-passed-hour="getIfPassedHour(hours[i].depart)"
                        :no-route="route.noParcours"
                        :no-stop="noStop"
                        :direction="direction"
                        :selected-date="selectedDate"
                        :stop="stop"
                        :route="route"
                        :hour="hours[i]"
                        :transfers="transfers"
                    />
                </li>
            </template>
            <li
                v-for="(stop, i) in futureStops"
                :key="i + passedStops.length"
                class="stop-item"
                :class="[hours[i + passedStops.length].arretNonDesservi ? 'is-unserved' : '']"
            >
                <TripStop
                    :ref="stop.noArret.toString() === noStop ? 'tripStop' : null"
                    :is-passed-hour="getIfPassedHour(hours[i + passedStops.length].depart)"
                    :no-route="route.noParcours"
                    :no-stop="noStop"
                    :direction="direction"
                    :selected-date="selectedDate"
                    :stop="stop"
                    :route="route"
                    :hour="hours[i + passedStops.length]"
                    :transfers="transfers"
                />
            </li>
        </ul>
        <button v-if="futureStops.length > 14" type="button" class="more-stops-button" @click="showHideStops('next')">
            <IconShowMore v-if="!showNext" class="more-icon" width="3" height="15" />
            {{ t(`show_hide_next_stops.${showNext ? 'hide' : 'show'}`) }}
        </button>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { isToday } from '@@/js/utils/dateFns';
import TripStop from '@/schedules/components/TripStop';

import IconShowMore from '@@/icons/show-more.svg';

const props = defineProps({
    route: {
        type: Object,
        default: () => ({}),
    },
    direction: {
        type: String,
        default: '',
    },
    noStop: {
        type: String,
        default: '',
    },
    selectedDate: {
        type: String,
        default: '',
    },
    hours: {
        type: Array,
        default: () => [],
    },
    transfers: {
        type: Array,
        default: () => [],
    },
    serviceClass: {
        type: String,
        required: true,
    },
});

const { t } = useI18n({
    messages: {
        fr: {
            show_hide_previous_stops: {
                show: 'Voir les arrêts précédents',
                hide: 'Cacher les arrêts précédents',
            },
            show_hide_next_stops: {
                show: 'Voir les arrêts suivants',
                hide: 'Cacher les arrêts suivants',
            },
        },
        en: {
            how_hide_previous_stops: {
                show: 'Show previous stops',
                hide: 'Hide previous stops',
            },
            show_hide_next_stops: {
                show: 'Show next stops',
                hide: 'Hide next stops',
            },
        },
    },
});

const tripStop = ref();
const showPrev = ref(false);
const showNext = ref(false);

const selectedStopIndex = computed(() => {
    const selectedStopIndex = props.hours.findIndex((hour) => {
        return hour.arret.noArret.toString() === props.noStop;
    });
    return selectedStopIndex;
});

const passedStops = computed(() => {
    const passedStops = [];
    props.hours.forEach((hour, i) => {
        if (i < selectedStopIndex.value) {
            passedStops.push(hour.arret);
        }
    });
    return passedStops;
});

const futureStops = computed(() => {
    let futureStops = [];
    const stops = [...props.hours];
    stops.forEach((hour, i) => {
        if (i >= selectedStopIndex.value) {
            futureStops.push(hour.arret);
        }
    });
    if (!showNext.value) {
        futureStops = futureStops.slice(0, 15);
    }
    return futureStops;
});

const stopListClasses = computed(() => {
    return {
        'has-previous-stops': passedStops.value.length !== 0 && !showPrev.value,
        'has-next-stops': futureStops.value.length > 14 && !showNext.value,
        'has-border-top': passedStops.value.length !== 0,
        'has-border-bottom': futureStops.value.length > 14,
        'short-list': !showNext.value && !showPrev.value,
    };
});

function showHideStops(direction) {
    if (tripStop.value && tripStop.value[0] && tripStop.value[0].transfer) {
        tripStop.value[0]?.transfer.close();
    }
    if (direction === 'prev') {
        showPrev.value = !showPrev.value;
    } else {
        showNext.value = !showNext.value;
    }
}

function getIfPassedHour(start) {
    const isHourToday = isToday(start);
    return !isHourToday ? false : new Date(start) < new Date();
}
</script>

<style lang="scss" scoped>
.stop-list {
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: auto;
    max-height: 935px;
    margin: 0;
    padding: 0 16px;
    @include breakpoint(medium) {
        max-height: 1146px;
    }
    &.short-list {
        overflow-y: hidden;
    }
    &.has-border-top {
        border-top: 1px solid $color-black-10;
    }
    &.has-border-bottom {
        border-bottom: 1px solid $color-black-10;
    }
    &.has-previous-stops::before {
        content: '';
        display: inline-block;
        position: absolute;
        top: 0;
        left: 21px;
        width: 6px;
        height: 30px;
        background-color: currentColor;
        border-radius: 8px 8px 0 0;
    }
}
@include colorize {
    .stop-list {
        color: get-color-variation('base');
    }
}
.stop-item {
    position: relative;
    margin-left: 24px;
    padding: 16px;
    border-bottom: 1px solid $color-black-10;
    &::before {
        content: '';
        display: inline-block;
        position: absolute;
        top: 20px;
        left: -24px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: currentColor;
    }
    &::after {
        content: '';
        display: inline-block;
        position: absolute;
        top: 24px;
        left: -19px;
        width: 6px;
        height: 100%;
        background-color: currentColor;
    }
    &.is-unserved::before {
        content: '';
        z-index: 1;
        position: absolute;
        top: 10px;
        left: -30px;
        width: 28px;
        height: 28px;
        background-color: transparent;
        background-image: url('@@/img/unserved-stop.svg');
        @include breakpoint(medium) {
            top: 18px;
        }
    }
    &:last-child {
        border-bottom: none;
        .has-next-stops & {
            &::after {
                content: '';
                display: inline-block;
                position: absolute;
                bottom: 0;
                width: 6px;
                height: 45px;
                background-color: currentColor;
                border-radius: 0 0 8px 8px;
            }
        }
    }
    &:last-of-type {
        &::after {
            display: none;
        }
    }
    @include breakpoint(medium) {
        padding: 15px 16px 16px;
    }
}
@include colorize {
    .stop-item {
        color: get-color-variation('base');
    }
}
.more-stops-button {
    @include rem-typo(14px);
    position: relative;
    width: 100%;
    padding: 20px 0;
    text-align: center;
    color: $color-blue-60;
    background-color: transparent;
    border: 0;
    svg {
        transition: all 300ms ease;
    }
}
@include colorize {
    .more-icon {
        fill: get-color-variation('base');
    }
}
.more-icon {
    position: absolute;
    left: 12px;
}
</style>
